import { addCalendarDays, fillTemplate, zonedDate, zonedTime, type TemplateValues } from '@fieldops/shared'
import { formatDateTime } from '../../lib/format.js'

/**
 * The small pure pieces of the compose window, kept apart from the
 * components so they can be tested without rendering one.
 */

const QUOTE_START = /\n*On [^\n]+ wrote:\n(?:>.*(?:\n|$))*$/

/**
 * A reply opens with the message it answers quoted under it. The Assistant
 * writes the part above the quote and never touches the quote itself, so a
 * draft is `own` and the quote goes back on after it.
 */
export function splitQuote(body: string): { own: string; quote: string } {
  const match = QUOTE_START.exec(body)
  if (!match) return { own: body, quote: '' }
  return { own: body.slice(0, match.index), quote: body.slice(match.index) }
}

export function joinQuote(own: string, quote: string): string {
  if (!quote) return own
  return `${own.trimEnd()}\n\n${quote.replace(/^\n+/, '')}`
}


export function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s@.]/gu, ' ')
    .split(/[\s@.]+/)
    .filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase()
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase()
}

export interface SendLaterOption {
  key: string
  label: string
  at: Date
}

/**
 * Tomorrow morning and next Monday morning, on the account's clock — a
 * message scheduled from a phone in another state still goes at 8am here.
 * Monday is dropped when it is tomorrow.
 */
export function sendLaterOptions(now: Date, timeZone: string): SendLaterOption[] {
  const today = zonedDate(now, timeZone)
  const tomorrow = addCalendarDays(today, 1)
  const weekday = new Date(`${today}T12:00:00Z`).getUTCDay()
  const monday = addCalendarDays(today, ((8 - weekday) % 7) || 7)
  const options: SendLaterOption[] = [
    { key: 'tomorrow', label: 'Tomorrow 8:00 am', at: zonedTime(tomorrow, '08:00', timeZone) },
  ]
  if (monday !== tomorrow) {
    options.push({ key: 'monday', label: 'Monday 8:00 am', at: zonedTime(monday, '08:00', timeZone) })
  }
  return options
}

/** A `datetime-local` value, read on the account's clock. */
export function fromLocalInput(value: string, timeZone: string): Date | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value)
  if (!match) return null
  return zonedTime(match[1]!, match[2]!, timeZone)
}

export function toLocalInput(at: Date, timeZone: string): string {
  const clock = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hour12: false })
    .format(at)
    .replace(/^24:/, '00:')
  return `${zonedDate(at, timeZone)}T${clock}`
}

/** "Tue 29 Sep, 8:00 am" on the account's clock. */
export function whenLabel(at: Date | string, timeZone: string): string {
  const date = typeof at === 'string' ? new Date(at) : at
  const day = new Intl.DateTimeFormat('en-AU', { timeZone, weekday: 'short', day: 'numeric', month: 'short' })
    .format(date)
    .replace(',', '')
  const time = new Intl.DateTimeFormat('en-AU', { timeZone, hour: 'numeric', minute: '2-digit', hour12: true })
    .format(date)
    .replace(/\s?([ap])\.?m\.?/i, ' $1m')
    .toLowerCase()
  return `${day}, ${time}`
}

export interface TemplateLike {
  subject: string | null
  bodyText: string | null
}

/**
 * A template into the message. Replace keeps a reply's quote and takes the
 * template's subject; Insert goes in at the cursor (or the end of what was
 * written above the quote) and only fills a subject that is empty.
 */
export function withTemplate(
  current: { subject: string; body: string },
  range: { start: number; end: number } | null,
  template: TemplateLike,
  mode: 'replace' | 'insert',
  values: TemplateValues,
): { value: { subject: string; body: string }; missing: string[]; caret: number } {
  const body = fillTemplate(template.bodyText ?? '', values)
  const subject = template.subject ? fillTemplate(template.subject, values) : null
  const { own, quote } = splitQuote(current.body)
  let next: string
  let caret: number
  if (mode === 'replace') {
    next = joinQuote(body.text, quote)
    caret = body.text.length
  } else {
    const start = Math.min(range?.start ?? own.length, own.length)
    const end = Math.min(Math.max(range?.end ?? start, start), own.length)
    next = `${current.body.slice(0, start)}${body.text}${current.body.slice(end)}`
    caret = start + body.text.length
  }
  const nextSubject = subject && (mode === 'replace' || current.subject.trim() === '') ? subject.text : current.subject
  return {
    value: { subject: nextSubject, body: next },
    missing: [...new Set([...(subject?.missing ?? []), ...body.missing])],
    caret,
  }
}

/**
 * The message a reply answers, quoted under it the way a mail app does:
 * "On 30 Oct 2026, 9:12 am, Margaret Chen wrote:" and each line marked.
 * `splitQuote` is how the Assistant later tells the reply from the quote.
 */
export function quoted(message: {
  fromName: string | null
  fromAddress: string
  strippedText: string | null
  bodyText: string | null
  receivedAt: Date | string
}): string {
  const text = message.strippedText ?? message.bodyText
  if (!text) return ''
  const when = formatDateTime(message.receivedAt)
  const who = message.fromName ?? message.fromAddress
  const lines = text.split('\n').map((line) => `> ${line}`)
  return `\n\nOn ${when}, ${who} wrote:\n${lines.join('\n')}`
}
